import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate, useSearchParams, Link } from "react-router-dom";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { api, formatMoney, formatApiError } from "../../lib/api";
import { normalizePhone, ContactIcons, MessageComposeDialog } from "../../components/admin/ContactPanel";
import { useCurrency } from "../../context/CurrencyContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "../../components/ui/table";
import {
  LayoutDashboard, Users, Car, Receipt, Globe2, Coins, ArrowRightLeft, Settings as Cog, ShieldCheck, Navigation, Sparkles, MessagesSquare, Mail, Phone, MessageCircle, Send, History as HistoryIcon, MessageSquareText, Flag,
} from "lucide-react";
import { toast } from "sonner";
import AdminKycPanel from "./AdminKycPanel";
import LiveTripMap from "../../components/LiveTripMap";

const nav = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, testId: "nav-admin-overview", end: true },
  { to: "/admin/users", label: "Users", icon: Users, testId: "nav-admin-users" },
  { to: "/admin/kyc", label: "KYC reviews", icon: ShieldCheck, testId: "nav-admin-kyc" },
  { to: "/admin/cars", label: "Cars", icon: Car, testId: "nav-admin-cars" },
  { to: "/admin/sponsorship", label: "Sponsorships", icon: Sparkles, testId: "nav-admin-sponsorship" },
  { to: "/admin/bookings", label: "Bookings", icon: Receipt, testId: "nav-admin-bookings" },
  { to: "/admin/history", label: "History", icon: HistoryIcon, testId: "nav-admin-history" },
  { to: "/admin/flagged", label: "Flagged", icon: Flag, testId: "nav-admin-flagged" },
  { to: "/admin/countries", label: "Countries", icon: Globe2, testId: "nav-admin-countries" },
  { to: "/admin/currencies", label: "Currencies", icon: Coins, testId: "nav-admin-currencies" },
  { to: "/admin/rates", label: "Exchange rates", icon: ArrowRightLeft, testId: "nav-admin-rates" },
  { to: "/admin/support", label: "Support", icon: MessagesSquare, testId: "nav-admin-support" },
  { to: "/admin/settings", label: "Settings", icon: Cog, testId: "nav-admin-settings" },
];

function Stat({ label, value, sub, to }) {
  const content = (
    <Card className={`border-border p-5 ${to ? "hover:border-foreground/30 hover:shadow-sm transition-shadow cursor-pointer" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="font-display text-2xl font-bold tracking-tight mt-1">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

function Overview() {
  const { currencies } = useCurrency();
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  if (!stats) return <div className="text-muted-foreground">Loading…</div>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-grid">
      <Stat label="Total users" value={stats.users} sub={`${stats.customers} customers · ${stats.owners} owners`} to="/admin/users" />
      <Stat label="Active cars" value={stats.cars_active} sub={`${stats.cars_pending} pending · ${stats.cars_total} total`} to="/admin/cars" />
      <Stat label="KYC to review" value={stats.kyc_pending || 0} sub="Identity verifications" to="/admin/kyc" />
      <Stat label="Bookings" value={stats.bookings_total} sub={`${stats.bookings_paid} paid`} to="/admin/bookings" />
      <Stat label="Commission earned" value={formatMoney(stats.commission_total, stats.default_currency, currencies)} sub="All time" to="/admin/bookings" />
    </div>
  );
}

function SuspendReasonDialog({ open, onOpenChange, onConfirm, label }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const confirm = async () => {
    if (reason.trim().length < 3) { toast.error("Please provide a reason (3+ chars)"); return; }
    setSubmitting(true);
    try { await onConfirm(reason.trim()); setReason(""); onOpenChange(false); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">{label}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Record why — based on reviews/complaints or your own observation. This is stored for your records.</p>
          <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Multiple renter complaints about late handovers and a damaged vehicle reported on 2 separate trips." data-testid="suspend-reason-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={confirm} disabled={submitting} data-testid="suspend-reason-confirm-btn">{submitting ? "Saving…" : "Confirm block"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UsersPanel() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("");
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [messageTarget, setMessageTarget] = useState(null);
  useEffect(() => { api.get("/admin/users").then(({ data }) => setUsers(data)); }, []);
  const filtered = users.filter((u) => !filter || u.email.toLowerCase().includes(filter.toLowerCase()) || u.name?.toLowerCase().includes(filter.toLowerCase()));
  const toggleSuspend = async (u, reason) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { suspended: !u.suspended, ...(reason ? { suspended_reason: reason } : {}) });
      setUsers(users.map((x) => x.id === u.id ? { ...x, suspended: !x.suspended, suspended_reason: reason || null } : x));
      toast.success("Updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setRole = async (u, role) => {
    try { await api.patch(`/admin/users/${u.id}`, { role }); setUsers(users.map((x) => x.id === u.id ? { ...x, role } : x)); toast.success("Role updated"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Remove ${u.name}'s account? They won't be able to log in again and their cars will be hidden. This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      if (data.ok) {
        setUsers(users.map((x) => x.id === u.id ? { ...x, removed: true } : x));
        toast.success("Account removed");
      } else {
        toast.error(data.message || "Could not delete this account");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card className="border-border p-6" data-testid="admin-users-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="font-display text-xl font-semibold tracking-tight">Users ({users.length})</h3>
        <Input placeholder="Search…" value={filter} onChange={(e) => setFilter(e.target.value)} className="max-w-xs" data-testid="users-filter-input" />
      </div>
      <Table>
        <TableHeader>
          <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>KYC</TableHead><TableHead>Rating</TableHead><TableHead>Bookings</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((u) => {
            const ratingValue = u.role === "owner" ? u.owner_rating : u.role === "customer" ? u.customer_rating : null;
            const ratingCount = u.role === "owner" ? u.owner_rating_count : u.role === "customer" ? u.customer_rating_count : null;
            return (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium max-w-[180px]">
                  <Link to={`/admin/history?actor_id=${u.id}`} className="truncate block hover:underline hover:text-[#2A9D8F]" title={`View ${u.name}'s activity`}>
                    {u.name}
                  </Link>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  <div>{u.email}</div>
                  <ContactIcons email={u.email} phone={u.phone} userId={u.id} name={u.name} isGuest={false} setMessageTarget={setMessageTarget} />
                </TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{u.role}</Badge></TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{u.kyc_status || "pending"}</Badge></TableCell>
                <TableCell className="text-sm text-muted-foreground">{ratingValue ? `${ratingValue.toFixed(1)} (${ratingCount})` : "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {u.total_bookings ? `${u.total_bookings} total` : "—"}
                  {u.cancellation_rate != null && u.cancellation_rate > 0 && (
                    <div className="text-[10px] text-amber-600 mt-0.5">{Math.round(u.cancellation_rate * 100)}% cancelled ({u.cancelled_bookings})</div>
                  )}
                </TableCell>
                <TableCell>
                  {u.removed ? <Badge variant="destructive">Removed</Badge> : u.suspended ? <Badge variant="destructive">Suspended</Badge> : <Badge>Active</Badge>}
                  {u.suspended && u.suspended_reason && !u.removed && <div className="text-[10px] text-muted-foreground mt-1 max-w-[160px]">{u.suspended_reason}</div>}
                </TableCell>
                <TableCell className="space-x-2 whitespace-nowrap">
                  {u.role !== "admin" && !u.removed && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/admin/cars?owner=${u.id}`)} data-testid={`view-cars-${u.id}`}>View cars</Button>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/admin/bookings?customer=${u.id}`)} data-testid={`view-bookings-${u.id}`}>View bookings</Button>
                      <Button size="sm" variant="outline" onClick={() => setRole(u, u.role === "owner" ? "customer" : "owner")}>{u.role === "owner" ? "Make customer" : "Make owner"}</Button>
                      {u.suspended ? (
                        <Button size="sm" variant="default" onClick={() => toggleSuspend(u)}>Unsuspend</Button>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => setSuspendTarget(u)}>Suspend</Button>
                      )}
                      <Button size="sm" variant="destructive" onClick={() => deleteUser(u)} data-testid={`delete-user-${u.id}`}>Delete</Button>
                    </>
                  )}
                  {u.role !== "admin" && u.removed && (
                    <span className="text-xs text-muted-foreground">No actions available</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <SuspendReasonDialog
        open={Boolean(suspendTarget)}
        onOpenChange={(o) => !o && setSuspendTarget(null)}
        label={suspendTarget ? `Block ${suspendTarget.name}?` : ""}
        onConfirm={(reason) => toggleSuspend(suspendTarget, reason)}
      />
      <MessageComposeDialog messageTarget={messageTarget} setMessageTarget={setMessageTarget} />
    </Card>
  );
}

function CarsPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const ownerFilter = searchParams.get("owner");
  const [cars, setCars] = useState([]);
  const [tab, setTab] = useState(ownerFilter ? "all" : "pending");
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [messageTarget, setMessageTarget] = useState(null);
  const load = () => {
    const params = { status: tab === "pending" ? "pending_or_review" : tab };
    if (ownerFilter) params.owner_id = ownerFilter;
    api.get("/cars", { params }).then(({ data }) => setCars(data));
  };
  useEffect(load, [tab, ownerFilter]);
  const approve = async (id) => { try { await api.post(`/admin/cars/${id}/approve`); toast.success("Approved"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const reject = async (id, reason) => { try { await api.post(`/admin/cars/${id}/reject`, { reason }); toast.success("Rejected"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const suspend = async (id, reason) => { try { await api.patch(`/cars/${id}`, { status: "suspended", ...(reason ? { suspension_reason: reason } : {}) }); toast.success("Suspended"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const toggleSponsor = async (id, current) => {
    try {
      await api.post(`/admin/cars/${id}/sponsor`, { sponsored: !current });
      toast.success(!current ? "Promoted to sponsored" : "Removed from sponsored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteCar = async (id) => {
    if (!window.confirm("Permanently delete this car listing? This cannot be undone.")) return;
    try { await api.delete(`/cars/${id}`); toast.success("Car deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const clearOwnerFilter = () => { searchParams.delete("owner"); setSearchParams(searchParams); };
  return (
    <Card className="border-border p-6" data-testid="admin-cars-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-display text-xl font-semibold tracking-tight">Cars</h3>
        <div className="flex gap-2">
          {["pending", "active", "rejected", "suspended", "all"].map((t) => (
            <Button key={t} size="sm" variant={tab === t ? "default" : "outline"} onClick={() => setTab(t)} data-testid={`car-tab-${t}`}>{t}</Button>
          ))}
        </div>
      </div>
      {ownerFilter && (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <Badge variant="outline">Filtered to one owner</Badge>
          <Button size="sm" variant="ghost" onClick={clearOwnerFilter} data-testid="clear-owner-filter">Clear filter</Button>
        </div>
      )}
      <Table>
        <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>City</TableHead><TableHead>Owner</TableHead><TableHead>Price</TableHead><TableHead>Status</TableHead><TableHead>Sponsored</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {cars.map((c) => (
            <TableRow key={c.id} data-testid={`admin-car-row-${c.id}`}>
              <TableCell className="font-medium">
                <div>{c.title}</div>
                {c.ownership_type && c.ownership_type !== "own" && (
                  <div className="text-[10px] mt-0.5 text-amber-600 font-medium capitalize">
                    {c.ownership_type === "company" ? "Company car" : "Third-party car"}
                    {c.authorization_doc && <span className="ml-1 text-green-600">· Auth doc uploaded</span>}
                    {!c.authorization_doc && <span className="ml-1 text-red-500">· No auth doc</span>}
                  </div>
                )}
                {c.ocr_status && (
                  <div className={`text-[10px] mt-0.5 font-medium ${c.ocr_status === "passed" ? "text-green-600" : c.ocr_status === "flagged" ? "text-amber-600" : c.ocr_status === "error" ? "text-red-500" : "text-muted-foreground"}`}>
                    OCR: {c.ocr_status}
                  </div>
                )}
                {[...(c.listing_flags || []), ...(c.ocr_flags || [])].length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {[...(c.listing_flags || []), ...(c.ocr_flags || [])].map((flag, i) => (
                      <div key={i} className="text-[10px] text-red-500 max-w-[200px]">⚠ {flag}</div>
                    ))}
                  </div>
                )}
                {c.ocr_results?.logbook?.plate_number && (
                  <div className="text-[10px] text-muted-foreground mt-0.5">Plate: {c.ocr_results.logbook.plate_number}</div>
                )}
                {c.documents?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {c.documents.map((d) => (
                      <a key={d.type} href={`/uploads/${d.file_path?.replace(/^\/?(uploads\/)?/, "")}`} target="_blank" rel="noreferrer"
                        className="text-[10px] underline text-accent">
                        {d.type === "logbook" ? "Carte Jaune" : d.type === "insurance" ? "Assurance" : "Contrôle Technique"}
                      </a>
                    ))}
                    {c.authorization_doc && (
                      <a href={`/uploads/${c.authorization_doc?.replace(/^\/?(uploads\/)?/, "")}`} target="_blank" rel="noreferrer"
                        className="text-[10px] underline text-amber-600">Auth letter</a>
                    )}
                  </div>
                )}
              </TableCell>
              <TableCell>{c.city}</TableCell>
              <TableCell className="text-muted-foreground">
                <div>{c.owner?.name || "—"}</div>
                {c.owner && <ContactIcons email={c.owner.email} phone={c.owner.phone} userId={c.owner.id} name={c.owner.name} isGuest={false} setMessageTarget={setMessageTarget} />}
              </TableCell>
              <TableCell>{c.price_per_day} {c.currency}</TableCell>
              <TableCell>
                <Badge variant="outline" className="capitalize">{c.status === "pending_kyc" ? "Pending verification" : c.status === "pending_review" ? "Pending review" : c.status}</Badge>
                {c.status === "suspended" && c.suspension_reason && <div className="text-[10px] text-muted-foreground mt-1 max-w-[160px]">{c.suspension_reason}</div>}
                {c.status === "rejected" && c.rejection_reason && <div className="text-[10px] text-muted-foreground mt-1 max-w-[160px]">{c.rejection_reason}</div>}
              </TableCell>
              <TableCell>
                <Button
                  size="sm"
                  variant={c.sponsored ? "default" : "outline"}
                  onClick={() => toggleSponsor(c.id, c.sponsored)}
                  className={c.sponsored ? "bg-[#E97A48] hover:bg-[#D86A38] text-white" : ""}
                  data-testid={`sponsor-toggle-${c.id}`}
                >
                  {c.sponsored ? "Sponsored ✓" : "Sponsor"}
                </Button>
              </TableCell>
              <TableCell className="space-x-2">
                {c.status !== "active" && <Button size="sm" onClick={() => approve(c.id)} data-testid={`approve-car-${c.id}`}>Approve</Button>}
                {["pending", "pending_review"].includes(c.status) && <Button size="sm" variant="outline" onClick={() => setRejectTarget(c)} data-testid={`reject-car-${c.id}`}>Reject</Button>}
                {c.status === "active" && <Button size="sm" variant="outline" onClick={() => setSuspendTarget(c)} data-testid={`suspend-car-${c.id}`}>Suspend</Button>}
                <Button size="sm" variant="destructive" onClick={() => deleteCar(c.id)} data-testid={`delete-car-${c.id}`}>Delete</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <SuspendReasonDialog
        open={Boolean(suspendTarget)}
        onOpenChange={(o) => !o && setSuspendTarget(null)}
        label={suspendTarget ? `Suspend "${suspendTarget.title}"?` : ""}
        onConfirm={(reason) => suspend(suspendTarget.id, reason)}
      />
      <SuspendReasonDialog
        open={Boolean(rejectTarget)}
        onOpenChange={(o) => !o && setRejectTarget(null)}
        label={rejectTarget ? `Reject "${rejectTarget.title}"?` : ""}
        onConfirm={(reason) => reject(rejectTarget.id, reason)}
      />
      <MessageComposeDialog messageTarget={messageTarget} setMessageTarget={setMessageTarget} />
    </Card>
  );
}
function BookingsPanel() {
  const { currencies } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerFilter = searchParams.get("customer");
  const [bookings, setBookings] = useState([]);
  const [refundingId, setRefundingId] = useState(null);
  const [trackingId, setTrackingId] = useState(null);
  const [messageTarget, setMessageTarget] = useState(null);
  const load = () => {
    const params = {};
    if (customerFilter) params.customer_id = customerFilter;
    api.get("/admin/bookings", { params }).then(({ data }) => setBookings(data));
  };
  useEffect(() => { load(); }, [customerFilter]);
  const clearCustomerFilter = () => { searchParams.delete("customer"); setSearchParams(searchParams); };
  const refund = async (id) => {
    if (!window.confirm("Refund this booking? This will reverse the payment.")) return;
    setRefundingId(id);
    try {
      const { data } = await api.post(`/bookings/${id}/refund`);
      toast.success(`Refund initiated (${data.refund?.method || "manual"}) — status: ${data.refund?.status || "pending"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRefundingId(null);
    }
  };
  const denyRefund = async (id) => {
    const reason = window.prompt("Reason for declining this refund request (shown to the customer):");
    if (reason === null) return;
    if (reason.trim().length < 3) { toast.error("Please provide a reason (3+ characters)"); return; }
    try {
      await api.post(`/admin/bookings/${id}/refund/deny`, { reason: reason.trim() });
      toast.success("Refund request declined");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const trackingBooking = bookings.find((b) => b.id === trackingId);
  return (
    <Card className="border-border p-6" data-testid="admin-bookings-panel">
      <h3 className="font-display text-xl font-semibold tracking-tight mb-4">All bookings ({bookings.length})</h3>
      {customerFilter && (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <Badge variant="outline">Filtered to one customer</Badge>
          <Button size="sm" variant="ghost" onClick={clearCustomerFilter} data-testid="clear-customer-filter">Clear filter</Button>
        </div>
      )}
      <div className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>Car</TableHead><TableHead>Customer</TableHead><TableHead>Owner</TableHead><TableHead>Booked On</TableHead><TableHead>Dates</TableHead><TableHead>Total</TableHead><TableHead>Earnings</TableHead><TableHead>Status</TableHead><TableHead>Payment</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {bookings.map((b) => {
            const now = Date.now();
            const inTripWindow = new Date(b.start_date).getTime() - 86400000 <= now && new Date(b.end_date).getTime() + 86400000 >= now;
            const canTrack = b.payment_status === "paid" && b.status === "confirmed" && inTripWindow;
            return (
              <TableRow key={b.id} data-testid={`admin-booking-${b.id}`}>
                <TableCell className="font-medium">{b.car_title}</TableCell>
                <TableCell className="text-muted-foreground max-w-[160px]">
                  <div className="truncate" title={b.customer_name}>{b.customer_name}</div>
                  <ContactIcons email={b.customer_email} phone={b.customer_phone} userId={b.customer_id} name={b.customer_name} isGuest={b.customer_is_guest} setMessageTarget={setMessageTarget} />
                </TableCell>
                <TableCell className="text-muted-foreground max-w-[160px]">
                  <div className="truncate" title={b.owner_name || ""}>{b.owner_name || "—"}</div>
                  <ContactIcons email={b.owner_email} phone={b.owner_phone} userId={b.owner_id} name={b.owner_name} isGuest={false} setMessageTarget={setMessageTarget} />
                </TableCell>
                <TableCell className="text-muted-foreground text-xs whitespace-nowrap">{b.created_at ? new Date(b.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—"}</TableCell>
                <TableCell className="text-muted-foreground">{b.start_date} → {b.end_date}</TableCell>
                <TableCell>{formatMoney(b.total, b.currency, currencies)}</TableCell>
                <TableCell className="text-muted-foreground">{b.commission != null ? formatMoney(b.commission, b.currency, currencies) : "—"}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="capitalize">{b.status.replace("_", " ")}</Badge>
                  {b.status === "cancelled" && b.cancellation_reason && (
                    <div className="text-[10px] text-muted-foreground mt-1 max-w-[180px]">Reason: {b.cancellation_reason}</div>
                  )}
                  {b.refund_status === "denied" && (
                    <div className="text-[10px] text-destructive mt-1 max-w-[180px]">Refund declined</div>
                  )}
                </TableCell>
                <TableCell><Badge variant={b.payment_status === "refunded" ? "destructive" : "outline"} className="capitalize">{b.payment_status}</Badge></TableCell>
                <TableCell className="space-x-1">
                  {canTrack && (
                    <Button size="sm" variant="outline" onClick={() => setTrackingId(b.id)} data-testid={`admin-track-${b.id}`}>
                      <Navigation size={12} className="mr-1" /> Track
                    </Button>
                  )}
                  {b.payment_status === "paid" && (
                    <Button size="sm" variant="outline" disabled={refundingId === b.id} onClick={() => refund(b.id)} data-testid={`refund-${b.id}`}>
                      {refundingId === b.id ? "Refunding…" : "Refund"}
                    </Button>
                  )}
                  {b.refund_status === "pending" && (
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => denyRefund(b.id)} data-testid={`deny-refund-${b.id}`}>
                      Deny
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </div>
      <LiveTripMap
        open={Boolean(trackingId)}
        onOpenChange={(o) => !o && setTrackingId(null)}
        bookingId={trackingId}
        title={trackingBooking?.car_title}
      />
      <MessageComposeDialog messageTarget={messageTarget} setMessageTarget={setMessageTarget} />
    </Card>
  );
}

function CountriesPanel() {
  const { reload } = useCurrency();
  const [countries, setCountries] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", currency_code: "", is_active: true });
  const load = () => api.get("/countries", { params: { active_only: false } }).then(({ data }) => setCountries(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.patch(`/countries/${editing}`, form);
      else await api.post("/countries", form);
      toast.success("Saved"); setOpen(false); setEditing(null); setForm({ code: "", name: "", currency_code: "", is_active: true });
      load(); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this country?")) return; try { await api.delete(`/countries/${id}`); toast.success("Deleted"); load(); reload(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <Card className="border-border p-6" data-testid="admin-countries-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-xl font-semibold tracking-tight">Countries</h3>
          <p className="text-sm text-muted-foreground">Markets where RydeXpress operates. Default cannot be deleted.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm({ code: "", name: "", currency_code: "", is_active: true }); } }}>
          <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-country-btn">Add country</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit country" : "Add country"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>ISO code (2 chars)</Label><Input maxLength={2} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="KE" data-testid="country-code-input" /></div>
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Kenya" data-testid="country-name-input" /></div>
              <div><Label>Currency code</Label><Input maxLength={3} value={form.currency_code} onChange={(e) => setForm({ ...form, currency_code: e.target.value.toUpperCase() })} placeholder="KES" data-testid="country-currency-input" /></div>
              <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /> <Label>Active</Label></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="country-save-btn">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Currency</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {countries.map((c) => (
            <TableRow key={c.id} data-testid={`country-row-${c.code}`}>
              <TableCell className="font-mono">{c.code}</TableCell>
              <TableCell className="font-medium">{c.name} {c.is_default && <Badge variant="outline" className="ml-2">default</Badge>}</TableCell>
              <TableCell className="font-mono">{c.currency_code}</TableCell>
              <TableCell>{c.is_active ? <Badge>Active</Badge> : <Badge variant="outline">Inactive</Badge>}</TableCell>
              <TableCell className="space-x-2">
                <Button size="sm" variant="outline" onClick={() => { setEditing(c.id); setForm({ code: c.code, name: c.name, currency_code: c.currency_code, is_active: c.is_active }); setOpen(true); }} data-testid={`edit-country-${c.code}`}>Edit</Button>
                {!c.is_default && <Button size="sm" variant="ghost" onClick={() => del(c.id)} data-testid={`delete-country-${c.code}`}>Delete</Button>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function CurrenciesPanel() {
  const { reload } = useCurrency();
  const [currencies, setCurrencies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", symbol: "", is_active: true });
  const load = () => api.get("/currencies", { params: { active_only: false } }).then(({ data }) => setCurrencies(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.patch(`/currencies/${editing}`, form);
      else await api.post("/currencies", form);
      toast.success("Saved"); setOpen(false); setEditing(null); setForm({ code: "", name: "", symbol: "", is_active: true });
      load(); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this currency?")) return; try { await api.delete(`/currencies/${id}`); toast.success("Deleted"); load(); reload(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <Card className="border-border p-6" data-testid="admin-currencies-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-xl font-semibold tracking-tight">Currencies</h3>
          <p className="text-sm text-muted-foreground">Supported currencies. Default cannot be deleted.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm({ code: "", name: "", symbol: "", is_active: true }); } }}>
          <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-currency-btn">Add currency</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit currency" : "Add currency"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Code (3 chars)</Label><Input maxLength={3} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="TZS" data-testid="currency-code-input" /></div>
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Tanzanian Shilling" data-testid="currency-name-input" /></div>
              <div><Label>Symbol</Label><Input value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} placeholder="TSh" data-testid="currency-symbol-input" /></div>
              <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /> <Label>Active</Label></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="currency-save-btn">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Symbol</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {currencies.map((c) => (
            <TableRow key={c.id} data-testid={`currency-row-${c.code}`}>
              <TableCell className="font-mono">{c.code}</TableCell>
              <TableCell className="font-medium">{c.name} {c.is_default && <Badge variant="outline" className="ml-2">default</Badge>}</TableCell>
              <TableCell className="font-mono">{c.symbol}</TableCell>
              <TableCell>{c.is_active ? <Badge>Active</Badge> : <Badge variant="outline">Inactive</Badge>}</TableCell>
              <TableCell className="space-x-2">
                <Button size="sm" variant="outline" onClick={() => { setEditing(c.id); setForm({ code: c.code, name: c.name, symbol: c.symbol, is_active: c.is_active }); setOpen(true); }} data-testid={`edit-currency-${c.code}`}>Edit</Button>
                {!c.is_default && <Button size="sm" variant="ghost" onClick={() => del(c.id)} data-testid={`delete-currency-${c.code}`}>Delete</Button>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function RatesPanel() {
  const { currencies } = useCurrency();
  const [rates, setRates] = useState([]);
  const [form, setForm] = useState({ from_currency: "", to_currency: "", rate: "" });
  const load = () => api.get("/exchange-rates").then(({ data }) => setRates(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.from_currency || !form.to_currency || !form.rate) { toast.error("All fields required"); return; }
    try {
      await api.post("/exchange-rates", { from_currency: form.from_currency, to_currency: form.to_currency, rate: parseFloat(form.rate) });
      toast.success("Rate saved"); setForm({ from_currency: "", to_currency: "", rate: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="border-border p-6" data-testid="admin-rates-panel">
      <div className="mb-6">
        <h3 className="font-display text-xl font-semibold tracking-tight">Exchange rates</h3>
        <p className="text-sm text-muted-foreground">Admin overrides take precedence over live rates from the API. Leave empty to use live rates only.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end mb-6 p-4 bg-muted rounded-md">
        <div><Label className="text-xs">From</Label><Input className="mt-1.5" value={form.from_currency} onChange={(e) => setForm({ ...form, from_currency: e.target.value.toUpperCase() })} placeholder="RWF" data-testid="rate-from-input" /></div>
        <div><Label className="text-xs">To</Label><Input className="mt-1.5" value={form.to_currency} onChange={(e) => setForm({ ...form, to_currency: e.target.value.toUpperCase() })} placeholder="USD" data-testid="rate-to-input" /></div>
        <div><Label className="text-xs">Rate (1 from = ? to)</Label><Input className="mt-1.5" type="number" step="0.000001" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="0.00078" data-testid="rate-value-input" /></div>
        <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="rate-save-btn">Save / Upsert</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>From</TableHead><TableHead>To</TableHead><TableHead>Rate</TableHead><TableHead>Source</TableHead><TableHead>Updated</TableHead></TableRow></TableHeader>
        <TableBody>
          {rates.map((r, i) => (
            <TableRow key={i}>
              <TableCell className="font-mono">{r.from_currency}</TableCell>
              <TableCell className="font-mono">{r.to_currency}</TableCell>
              <TableCell>{r.rate}</TableCell>
              <TableCell><Badge variant={r.source === "admin" ? "default" : "outline"}>{r.source}</Badge></TableCell>
              <TableCell className="text-muted-foreground text-xs">{r.updated_at?.split("T")[0]}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function PayoutDestinationsPanel() {
  const [momo, setMomo] = useState({ provider: "MTN Mobile Money", name: "Rydexpress Ltd", settlement_bank: "Bank of Kigali", settlement_account: "100268875721", settlement_currency: "RWF" });
  const [bank, setBank] = useState({ bank_name: "Bank of Kigali", account_holder: "Rydexpress Ltd", account_number_rwf: "", iban_rwf: "", account_number_usd: "", iban_usd: "", branch: "Kigali - Gasabo", swift: "BKIGRWRW" });
  const [contact, setContact] = useState({ email: "", phone: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => {
      if (data.payout_momo) setMomo((prev) => ({ ...prev, ...data.payout_momo }));
      if (data.payout_bank) setBank((prev) => ({ ...prev, ...data.payout_bank }));
      if (data.platform_contact) setContact((prev) => ({ ...prev, ...data.platform_contact }));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/payout_momo", { key: "payout_momo", value: momo });
      await api.put("/settings/payout_bank", { key: "payout_bank", value: bank });
      await api.put("/settings/platform_contact", { key: "platform_contact", value: contact });
      toast.success("Payout destinations saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-border p-6 max-w-2xl" data-testid="payout-destinations-panel">
      <h3 className="font-display text-xl font-semibold tracking-tight">Platform payout destinations</h3>
      <p className="text-sm text-muted-foreground mt-1">Booking fees settle to Bank of Kigali. MoMo payments settle to the RWF account; card payments in USD settle to the USD account, others are converted to RWF.</p>

      <div className="mt-5 space-y-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Mobile Money settlement</div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Provider</Label><Input className="mt-1.5" value={momo.provider} onChange={(e) => setMomo({ ...momo, provider: e.target.value })} data-testid="momo-provider-input" /></div>
          <div><Label className="text-xs">Merchant name</Label><Input className="mt-1.5" value={momo.name} onChange={(e) => setMomo({ ...momo, name: e.target.value })} data-testid="momo-name-input" /></div>
          <div><Label className="text-xs">Settlement bank</Label><Input className="mt-1.5" value={momo.settlement_bank} onChange={(e) => setMomo({ ...momo, settlement_bank: e.target.value })} data-testid="momo-settlement-bank-input" /></div>
          <div><Label className="text-xs">Settlement account (RWF)</Label><Input className="mt-1.5" value={momo.settlement_account} onChange={(e) => setMomo({ ...momo, settlement_account: e.target.value })} data-testid="momo-settlement-account-input" /></div>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Bank of Kigali accounts</div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Bank name</Label><Input className="mt-1.5" value={bank.bank_name} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} data-testid="bank-name-input" /></div>
          <div><Label className="text-xs">Account holder</Label><Input className="mt-1.5" value={bank.account_holder} onChange={(e) => setBank({ ...bank, account_holder: e.target.value })} data-testid="bank-holder-input" /></div>
          <div><Label className="text-xs">RWF account number</Label><Input className="mt-1.5" value={bank.account_number_rwf} onChange={(e) => setBank({ ...bank, account_number_rwf: e.target.value })} data-testid="bank-account-rwf-input" /></div>
          <div><Label className="text-xs">RWF IBAN</Label><Input className="mt-1.5" value={bank.iban_rwf} onChange={(e) => setBank({ ...bank, iban_rwf: e.target.value })} data-testid="bank-iban-rwf-input" /></div>
          <div><Label className="text-xs">USD account number</Label><Input className="mt-1.5" value={bank.account_number_usd} onChange={(e) => setBank({ ...bank, account_number_usd: e.target.value })} data-testid="bank-account-usd-input" /></div>
          <div><Label className="text-xs">USD IBAN</Label><Input className="mt-1.5" value={bank.iban_usd} onChange={(e) => setBank({ ...bank, iban_usd: e.target.value })} data-testid="bank-iban-usd-input" /></div>
          <div><Label className="text-xs">Branch</Label><Input className="mt-1.5" value={bank.branch} onChange={(e) => setBank({ ...bank, branch: e.target.value })} data-testid="bank-branch-input" /></div>
          <div><Label className="text-xs">SWIFT</Label><Input className="mt-1.5" value={bank.swift} onChange={(e) => setBank({ ...bank, swift: e.target.value })} data-testid="bank-swift-input" /></div>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Public contact</div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Email</Label><Input className="mt-1.5" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="contact-email-input" /></div>
          <div><Label className="text-xs">Phone</Label><Input className="mt-1.5" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="contact-phone-input" /></div>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="mt-6 bg-primary hover:bg-primary/90" data-testid="payout-save-btn">
        {saving ? "Saving…" : "Save payout destinations"}
      </Button>
    </Card>
  );
}

function SponsorshipOrdersPanel() {
  const { settings } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/admin/sponsorship/orders").then(({ data }) => setOrders(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const markPaid = async (order) => {
    if (!window.confirm(`Confirm you have received ${order.amount} ${order.currency} for this sponsorship?`)) return;
    try {
      await api.post(`/admin/sponsorship/orders/${order.id}/mark-paid`);
      toast.success(`Marked paid — car promoted for ${order.duration_days || 365} days`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="text-muted-foreground">Loading…</div>;
  return (
    <Card className="border-border p-6" data-testid="admin-sponsorship-panel">
      <h3 className="font-display text-xl font-semibold tracking-tight">Sponsorship orders</h3>
      <p className="text-xs text-muted-foreground mt-1">Owners pay RWF {(settings?.sponsorship_price_rwf ?? 50000).toLocaleString()} to promote a car for {settings?.sponsorship_duration_days ?? 365} days. After payment lands in MoMo 0795883652 or the Equity account, mark the order as paid here.</p>
      <Table className="mt-4">
        <TableHeader><TableRow><TableHead>Car</TableHead><TableHead>Owner</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {orders.length === 0 ? (
            <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No sponsorship orders yet.</TableCell></TableRow>
          ) : orders.map((o) => (
            <TableRow key={o.id} data-testid={`sponsorship-order-${o.id}`}>
              <TableCell className="font-medium font-mono text-xs">{o.car_id.slice(0, 8)}…</TableCell>
              <TableCell className="font-mono text-xs">{o.owner_id.slice(0, 8)}…</TableCell>
              <TableCell>{o.amount} {o.currency}</TableCell>
              <TableCell><Badge variant={o.status === "paid" ? "default" : "outline"} className="capitalize">{o.status.replace("_", " ")}</Badge></TableCell>
              <TableCell className="text-xs text-muted-foreground">{o.created_at?.slice(0, 10)}</TableCell>
              <TableCell>
                {o.status === "pending_payment" && (
                  <Button size="sm" onClick={() => markPaid(o)} className="bg-[#E97A48] hover:bg-[#D86A38] text-white" data-testid={`mark-paid-${o.id}`}>Mark paid</Button>
                )}
                {o.status === "paid" && o.sponsored_until && (
                  <span className="text-xs text-muted-foreground">Active until {o.sponsored_until.slice(0, 10)}</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function PaymentGatewaysPanel() {
  const [momoGateway, setMomoGateway] = useState("mtn");
  const [cardGateway, setCardGateway] = useState("urubutopay");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => {
      if (data.payment_gateways) {
        setMomoGateway(data.payment_gateways.momo_gateway || "mtn");
        setCardGateway(data.payment_gateways.card_gateway || "urubutopay");
      }
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/payment_gateways", {
        key: "payment_gateways",
        value: { momo_gateway: momoGateway, card_gateway: cardGateway },
      });
      toast.success("Payment gateway settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const momoStatus = {
    mtn: { label: "Active — MTN MoMo sandbox", color: "text-green-600" },
    urubutopay: { label: "Pending — awaiting UrubutoPay credentials", color: "text-amber-600" },
  };
  const cardStatus = {
    urubutopay: { label: "Pending — awaiting UrubutoPay credentials", color: "text-amber-600" },
  };

  return (
    <Card className="border-border p-6 max-w-xl" data-testid="payment-gateways-panel">
      <h3 className="font-display text-xl font-semibold tracking-tight">Payment gateways</h3>
      <p className="text-sm text-muted-foreground mt-1">Choose which provider handles each payment type. Changes take effect immediately.</p>
      <div className="mt-5 space-y-4">
        <div className="rounded-lg border border-border p-4 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium">Mobile money (MoMo)</div>
              <div className="text-xs text-muted-foreground">MTN MoMo, Airtel Money</div>
            </div>
            <select
              value={momoGateway}
              onChange={(e) => setMomoGateway(e.target.value)}
              className="text-sm border border-border rounded-md px-2 py-1.5 bg-background"
              data-testid="momo-gateway-select"
            >
              <option value="mtn">MTN MoMo API</option>
              <option value="urubutopay">UrubutoPay (BK)</option>
            </select>
          </div>
          <div className={`text-xs ${momoStatus[momoGateway]?.color}`}>
            {momoStatus[momoGateway]?.label}
          </div>
        </div>

        <div className="rounded-lg border border-border p-4 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium">Card payments</div>
              <div className="text-xs text-muted-foreground">Visa, Mastercard, AMEX</div>
            </div>
            <select
              value={cardGateway}
              onChange={(e) => setCardGateway(e.target.value)}
              className="text-sm border border-border rounded-md px-2 py-1.5 bg-background"
              data-testid="card-gateway-select"
            >
              <option value="urubutopay">UrubutoPay (BK)</option>
            </select>
          </div>
          <div className={`text-xs ${cardStatus[cardGateway]?.color}`}>
            {cardStatus[cardGateway]?.label}
          </div>
        </div>
      </div>
      <Button
        onClick={save}
        disabled={saving}
        className="mt-4 bg-primary hover:bg-primary/90"
        data-testid="payment-gateways-save-btn"
      >
        {saving ? "Saving…" : "Save gateway settings"}
      </Button>
    </Card>
  );
}

function SettingsPanel() {
  const { reload, settings } = useCurrency();
  const [commission, setCommission] = useState("");
  const [defaultCountry, setDefaultCountry] = useState("");
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [platformName, setPlatformName] = useState("");
  useEffect(() => {
    if (settings) {
      setCommission(settings.commission_rate);
      setDefaultCountry(settings.default_country);
      setDefaultCurrency(settings.default_currency);
      setPlatformName(settings.platform_name);
    }
  }, [settings]);

  const saveCommission = async () => {
    const v = parseFloat(commission);
    if (isNaN(v) || v < 0 || v > 1) { toast.error("Commission must be between 0 and 1 (e.g., 0.10 = 10%)"); return; }
    try { await api.put("/settings/commission_rate", { key: "commission_rate", value: v }); toast.success("Commission updated"); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveDefaults = async () => {
    try {
      await api.put("/settings/default_country", { key: "default_country", value: defaultCountry.toUpperCase() });
      await api.put("/settings/default_currency", { key: "default_currency", value: defaultCurrency.toUpperCase() });
      await api.put("/settings/platform_name", { key: "platform_name", value: platformName });
      toast.success("Settings saved"); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [sponsorPrice, setSponsorPrice] = useState("");
  const [sponsorDuration, setSponsorDuration] = useState("");
  useEffect(() => {
    if (settings) {
      setSponsorPrice(settings.sponsorship_price_rwf);
      setSponsorDuration(settings.sponsorship_duration_days);
    }
  }, [settings]);
  const [authDocRequired, setAuthDocRequired] = useState(true);
  useEffect(() => {
    if (settings) {
      setAuthDocRequired(settings.authorization_doc_required ?? true);
    }
  }, [settings]);
  const toggleAuthDocRequired = async (checked) => {
    setAuthDocRequired(checked);
    try {
      await api.put("/settings/authorization_doc_required", { key: "authorization_doc_required", value: checked });
      toast.success(checked ? "Authorization document is now required" : "Authorization document is now optional");
      reload();
    } catch (e) {
      setAuthDocRequired(!checked);
      toast.error(formatApiError(e));
    }
  };
  const [carsAutoPublish, setCarsAutoPublish] = useState(false);
  useEffect(() => {
    if (settings) {
      setCarsAutoPublish(settings.cars_auto_publish ?? false);
    }
  }, [settings]);
  const toggleCarsAutoPublish = async (checked) => {
    setCarsAutoPublish(checked);
    try {
      await api.put("/settings/cars_auto_publish", { key: "cars_auto_publish", value: checked });
      toast.success(checked ? "New car listings now go live automatically" : "New car listings now require admin approval");
      reload();
    } catch (e) {
      setCarsAutoPublish(!checked);
      toast.error(formatApiError(e));
    }
  };

  const [renterTermsText, setRenterTermsText] = useState("");
  const [hostTermsText, setHostTermsText] = useState("");
  const [privacyPolicyText, setPrivacyPolicyText] = useState("");
  const [savingLegal, setSavingLegal] = useState(false);
  useEffect(() => {
    if (settings) {
      setRenterTermsText(settings.legal_renter_terms || "");
      setHostTermsText(settings.legal_host_terms || "");
      setPrivacyPolicyText(settings.legal_privacy_policy || "");
    }
  }, [settings]);
  const [aboutText, setAboutText] = useState("");
  const [blogText, setBlogText] = useState("");
  const [careersText, setCareersText] = useState("");
  const [pressText, setPressText] = useState("");
  const [helpText, setHelpText] = useState("");
  const [safetyText, setSafetyText] = useState("");
  useEffect(() => {
    if (settings) {
      setAboutText(settings.content_about || "");
      setBlogText(settings.content_blog || "");
      setCareersText(settings.content_careers || "");
      setPressText(settings.content_press || "");
      setHelpText(settings.content_help || "");
      setSafetyText(settings.content_safety || "");
    }
  }, [settings]);
  const saveLegalDoc = async (key, value, label) => {
    setSavingLegal(true);
    try {
      await api.put(`/settings/${key}`, { key, value });
      toast.success(`${label} updated`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingLegal(false);
    }
  };
  const saveSponsorship = async () => {
    const price = parseFloat(sponsorPrice);
    const days = parseInt(sponsorDuration, 10);
    if (isNaN(price) || price < 0) { toast.error("Price must be a positive number"); return; }
    if (isNaN(days) || days < 1) { toast.error("Duration must be at least 1 day"); return; }
    try {
      await api.put("/settings/sponsorship_price_rwf", { key: "sponsorship_price_rwf", value: price });
      await api.put("/settings/sponsorship_duration_days", { key: "sponsorship_duration_days", value: days });
      toast.success("Sponsorship settings saved"); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [negFloorPct, setNegFloorPct] = useState("");
  const [negMaxRounds, setNegMaxRounds] = useState("");
  const [negExpiryHours, setNegExpiryHours] = useState("");
  useEffect(() => {
    api.get("/admin/negotiation-settings").then(({ data }) => {
      setNegFloorPct(data.floor_pct_default);
      setNegMaxRounds(data.max_rounds);
      setNegExpiryHours(data.expiry_hours);
    }).catch(() => {});
  }, []);
  const saveNegotiationSettings = async () => {
    const floor = parseFloat(negFloorPct);
    const rounds = parseInt(negMaxRounds, 10);
    const hrs = parseInt(negExpiryHours, 10);
    if (isNaN(floor) || floor < 0 || floor > 100) { toast.error("Floor must be between 0 and 100"); return; }
    if (isNaN(rounds) || rounds < 1) { toast.error("Max rounds must be at least 1"); return; }
    if (isNaN(hrs) || hrs < 1) { toast.error("Expiry hours must be at least 1"); return; }
    try {
      await api.put("/settings/negotiation_floor_pct_default", { key: "negotiation_floor_pct_default", value: floor });
      await api.put("/settings/negotiation_max_rounds", { key: "negotiation_max_rounds", value: rounds });
      await api.put("/settings/negotiation_expiry_hours", { key: "negotiation_expiry_hours", value: hrs });
      toast.success("Negotiation settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [docSettings, setDocSettings] = useState({});
  const [verificationMode, setVerificationMode] = useState("backend");
  const [identityDocSettings, setIdentityDocSettings] = useState({ status: "required", max_size_mb: null });
  const [identityVerificationMode, setIdentityVerificationMode] = useState("backend");
  const [imageSettings, setImageSettings] = useState({ min_images: 4, max_images: 10 });
  useEffect(() => {
    if (settings) {
      setDocSettings(settings.document_settings || {});
      setVerificationMode(settings.document_verification_mode || "backend");
      setIdentityDocSettings(settings.identity_document_settings || { status: "required", max_size_mb: null });
      setIdentityVerificationMode(settings.identity_verification_mode || "backend");
      setImageSettings(settings.image_settings || { min_images: 4, max_images: 10 });
    }
  }, [settings]);
  const updateDocSetting = (type, field, value) => {
    setDocSettings((prev) => ({ ...prev, [type]: { ...prev[type], [field]: value } }));
  };
  const updateIdentityDocSetting = (field, value) => {
    setIdentityDocSettings((prev) => ({ ...prev, [field]: value }));
  };
  const updateImageSetting = (field, value) => {
    setImageSettings((prev) => ({ ...prev, [field]: value }));
  };
  const saveDocumentSettings = async () => {
    try {
      await api.put("/settings/document_settings", { key: "document_settings", value: docSettings });
      await api.put("/settings/document_verification_mode", { key: "document_verification_mode", value: verificationMode });
      await api.put("/settings/identity_document_settings", { key: "identity_document_settings", value: identityDocSettings });
      await api.put("/settings/identity_verification_mode", { key: "identity_verification_mode", value: identityVerificationMode });
      await api.put("/settings/image_settings", { key: "image_settings", value: imageSettings });
      toast.success("Document settings saved");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-settings-panel">
      <Card className="border-border p-6 max-w-xl">
        <h3 className="font-display text-xl font-semibold tracking-tight">Commission rate</h3>
        <p className="text-sm text-muted-foreground mt-1">Default is 0.10 (10%). Deducted from the owner's payout on every paid booking — the customer is charged the full listed price.</p>
        <div className="mt-4 flex items-end gap-3">
          <div className="flex-1">
            <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Commission (fraction)</Label>
            <Input className="mt-1.5" type="number" min={0} max={1} step="0.01" value={commission} onChange={(e) => setCommission(e.target.value)} data-testid="commission-input" />
          </div>
          <div className="text-sm pb-2.5">= <span className="font-display font-semibold">{(parseFloat(commission || 0) * 100).toFixed(1)}%</span></div>
        </div>
        <Button onClick={saveCommission} className="mt-4 bg-primary hover:bg-primary/90" data-testid="commission-save-btn">Save commission</Button>
      </Card>

      <Card className="border-border p-6 max-w-xl">
        <h3 className="font-display text-xl font-semibold tracking-tight">Platform defaults</h3>
        <div className="mt-4 space-y-3">
          <div><Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Platform name</Label><Input className="mt-1.5" value={platformName} onChange={(e) => setPlatformName(e.target.value)} data-testid="platform-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Default country code</Label><Input className="mt-1.5" maxLength={2} value={defaultCountry} onChange={(e) => setDefaultCountry(e.target.value.toUpperCase())} data-testid="default-country-input" /></div>
            <div><Label className="text-xs">Default currency code</Label><Input className="mt-1.5" maxLength={3} value={defaultCurrency} onChange={(e) => setDefaultCurrency(e.target.value.toUpperCase())} data-testid="default-currency-input" /></div>
          </div>
        </div>
        <Button onClick={saveDefaults} className="mt-4 bg-primary hover:bg-primary/90" data-testid="defaults-save-btn">Save defaults</Button>
      </Card>

      <Card className="border-border p-6 max-w-xl">
        <h3 className="font-display text-xl font-semibold tracking-tight">Sponsorship</h3>
        <p className="text-sm text-muted-foreground mt-1">Price and duration owners pay for the SPONSORED ★ badge. Changes only affect new orders — already-paid orders keep their original duration.</p>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Price (RWF)</Label><Input className="mt-1.5" type="number" min={0} step="1000" value={sponsorPrice} onChange={(e) => setSponsorPrice(e.target.value)} data-testid="sponsorship-price-input" /></div>
          <div><Label className="text-xs">Duration (days)</Label><Input className="mt-1.5" type="number" min={1} value={sponsorDuration} onChange={(e) => setSponsorDuration(e.target.value)} data-testid="sponsorship-duration-input" /></div>
        </div>
        <Button onClick={saveSponsorship} className="mt-4 bg-primary hover:bg-primary/90" data-testid="sponsorship-save-btn">Save sponsorship settings</Button>
      </Card>
      <Card className="border-border p-6 max-w-xl">
        <h3 className="font-display text-xl font-semibold tracking-tight">Vehicle ownership verification</h3>
        <p className="text-sm text-muted-foreground mt-1">
          When required, owners listing a company or third-party car must upload an authorization/consent letter before the listing can be submitted. Turn this off to reduce friction during launch — the feature stays intact and can be re-enabled anytime.
        </p>
        <div className="mt-4 flex items-center justify-between max-w-sm">
          <Label className="text-sm">Require authorization/consent letter</Label>
          <Switch
            checked={authDocRequired}
            onCheckedChange={toggleAuthDocRequired}
            data-testid="auth-doc-required-toggle"
          />
        </div>
      </Card>
      <Card className="border-border p-6 max-w-xl">
        <h3 className="font-display text-xl font-semibold tracking-tight">New listing publication</h3>
        <p className="text-sm text-muted-foreground mt-1">
          By default, new car listings require admin review before they appear on the marketplace. Turning this on publishes new listings immediately — OCR document checks still run, but nothing blocks the listing from going live. You can still suspend or remove any listing afterward from the Cars tab.
        </p>
        <div className="mt-4 flex items-center justify-between max-w-sm">
          <Label className="text-sm">Publish new listings automatically</Label>
          <Switch
            checked={carsAutoPublish}
            onCheckedChange={toggleCarsAutoPublish}
            data-testid="cars-auto-publish-toggle"
          />
        </div>
      </Card>
      <Card className="border-border p-6 max-w-2xl">
        <h3 className="font-display text-xl font-semibold tracking-tight">Legal documents</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Edit the text shown on the public Renter Terms, Host Terms, and Privacy Policy pages. Plain text — leave a blank line between sections to keep paragraph spacing.
        </p>
        <div className="mt-4 space-y-2">
          <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Renter Terms & Conditions</Label>
          <Textarea rows={8} value={renterTermsText} onChange={(e) => setRenterTermsText(e.target.value)} data-testid="legal-renter-terms-textarea" />
          <Button onClick={() => saveLegalDoc("legal_renter_terms", renterTermsText, "Renter Terms")} disabled={savingLegal} className="bg-primary hover:bg-primary/90" data-testid="legal-renter-terms-save-btn">Save Renter Terms</Button>
        </div>
        <div className="mt-6 space-y-2">
          <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Host Terms & Conditions</Label>
          <Textarea rows={8} value={hostTermsText} onChange={(e) => setHostTermsText(e.target.value)} data-testid="legal-host-terms-textarea" />
          <Button onClick={() => saveLegalDoc("legal_host_terms", hostTermsText, "Host Terms")} disabled={savingLegal} className="bg-primary hover:bg-primary/90" data-testid="legal-host-terms-save-btn">Save Host Terms</Button>
        </div>
        <div className="mt-6 space-y-2">
          <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Privacy Policy</Label>
          <Textarea rows={8} value={privacyPolicyText} onChange={(e) => setPrivacyPolicyText(e.target.value)} data-testid="legal-privacy-policy-textarea" />
          <Button onClick={() => saveLegalDoc("legal_privacy_policy", privacyPolicyText, "Privacy Policy")} disabled={savingLegal} className="bg-primary hover:bg-primary/90" data-testid="legal-privacy-policy-save-btn">Save Privacy Policy</Button>
        </div>
      </Card>
      <Card className="border-border p-6 max-w-2xl">
        <h3 className="font-display text-xl font-semibold tracking-tight">Content pages</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Edit the text shown on the footer's Company and Support pages.
        </p>
        <div className="mt-4 space-y-2">
          <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">About</Label>
          <Textarea rows={5} value={aboutText} onChange={(e) => setAboutText(e.target.value)} data-testid="content-about-textarea" />
          <Button onClick={() => saveLegalDoc("content_about", aboutText, "About")} disabled={savingLegal} className="bg-primary hover:bg-primary/90" data-testid="content-about-save-btn">Save About</Button>
        </div>
        <div className="mt-6 space-y-2">
          <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Blog</Label>
          <Textarea rows={5} value={blogText} onChange={(e) => setBlogText(e.target.value)} data-testid="content-blog-textarea" />
          <Button onClick={() => saveLegalDoc("content_blog", blogText, "Blog")} disabled={savingLegal} className="bg-primary hover:bg-primary/90" data-testid="content-blog-save-btn">Save Blog</Button>
        </div>
        <div className="mt-6 space-y-2">
          <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Careers</Label>
          <Textarea rows={5} value={careersText} onChange={(e) => setCareersText(e.target.value)} data-testid="content-careers-textarea" />
          <Button onClick={() => saveLegalDoc("content_careers", careersText, "Careers")} disabled={savingLegal} className="bg-primary hover:bg-primary/90" data-testid="content-careers-save-btn">Save Careers</Button>
        </div>
        <div className="mt-6 space-y-2">
          <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Press</Label>
          <Textarea rows={5} value={pressText} onChange={(e) => setPressText(e.target.value)} data-testid="content-press-textarea" />
          <Button onClick={() => saveLegalDoc("content_press", pressText, "Press")} disabled={savingLegal} className="bg-primary hover:bg-primary/90" data-testid="content-press-save-btn">Save Press</Button>
        </div>
        <div className="mt-6 space-y-2">
          <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Help Center</Label>
          <Textarea rows={5} value={helpText} onChange={(e) => setHelpText(e.target.value)} data-testid="content-help-textarea" />
          <Button onClick={() => saveLegalDoc("content_help", helpText, "Help Center")} disabled={savingLegal} className="bg-primary hover:bg-primary/90" data-testid="content-help-save-btn">Save Help Center</Button>
        </div>
        <div className="mt-6 space-y-2">
          <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Safety</Label>
          <Textarea rows={5} value={safetyText} onChange={(e) => setSafetyText(e.target.value)} data-testid="content-safety-textarea" />
          <Button onClick={() => saveLegalDoc("content_safety", safetyText, "Safety")} disabled={savingLegal} className="bg-primary hover:bg-primary/90" data-testid="content-safety-save-btn">Save Safety</Button>
        </div>
      </Card>

      <Card className="border-border p-6 max-w-xl">
        <h3 className="font-display text-xl font-semibold tracking-tight">Price negotiation</h3>
        <p className="text-sm text-muted-foreground mt-1">Floor is the minimum % of the listing price a renter may propose — never shown to renters. Max rounds caps how many back-and-forth offers a negotiation allows before it auto-closes.</p>
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Floor (% of listing)</Label><Input className="mt-1.5" type="number" min={0} max={100} value={negFloorPct} onChange={(e) => setNegFloorPct(e.target.value)} data-testid="negotiation-floor-input" /></div>
            <div><Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Max rounds</Label><Input className="mt-1.5" type="number" min={1} value={negMaxRounds} onChange={(e) => setNegMaxRounds(e.target.value)} data-testid="negotiation-rounds-input" /></div>
          </div>
          <div><Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Offer expiry (hours)</Label><Input className="mt-1.5" type="number" min={1} value={negExpiryHours} onChange={(e) => setNegExpiryHours(e.target.value)} data-testid="negotiation-expiry-input" /></div>
        </div>
        <Button onClick={saveNegotiationSettings} className="mt-4 bg-primary hover:bg-primary/90" data-testid="negotiation-settings-save-btn">Save negotiation settings</Button>
      </Card>

      <Card className="border-border p-6 max-w-xl" data-testid="document-settings-panel">
        <h3 className="font-display text-xl font-semibold tracking-tight">Vehicle document requirements</h3>
        <p className="text-sm text-muted-foreground mt-1">Control which documents owners must upload, and the max file size for each. In "Backend" mode, listings never block on missing documents — admin instead sees a remark on the Cars page and can follow up with the owner directly.</p>
        <div className="mt-4 space-y-4">
          {Object.entries(docSettings).map(([type, cfg]) => (
            <div key={type} className="border border-border rounded-md p-3">
              <div className="font-medium text-sm mb-2">{cfg.label || type}</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Status</Label>
                  <select
                    className="mt-1.5 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                    value={cfg.status || "optional"}
                    onChange={(e) => updateDocSetting(type, "status", e.target.value)}
                    data-testid={`doc-status-${type}`}
                  >
                    <option value="required">Required</option>
                    <option value="optional">Optional</option>
                    <option value="not_necessary">Not Necessary</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Max size (MB)</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input
                      type="number"
                      min={1}
                      disabled={cfg.max_size_mb === null || cfg.max_size_mb === undefined}
                      value={cfg.max_size_mb ?? ""}
                      onChange={(e) => updateDocSetting(type, "max_size_mb", e.target.value === "" ? null : Number(e.target.value))}
                      data-testid={`doc-maxsize-${type}`}
                    />
                    <label className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={cfg.max_size_mb === null || cfg.max_size_mb === undefined}
                        onChange={(e) => updateDocSetting(type, "max_size_mb", e.target.checked ? null : 10)}
                      />
                      No limit
                    </label>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4">
          <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Vehicle document verification mode</Label>
          <select
            className="mt-1.5 w-full h-9 rounded-md border border-input bg-background px-3 text-sm max-w-md"
            value={verificationMode}
            onChange={(e) => setVerificationMode(e.target.value)}
            data-testid="document-verification-mode-select"
          >
            <option value="backend">Backend (flexible — never blocks submission, admin follows up on flags)</option>
            <option value="frontend">Frontend (strict — required documents/expiry block submission)</option>
          </select>
        </div>

        <div className="mt-6 border-t border-border pt-6">
          <div className="font-medium text-sm mb-2">Identity document (National ID / Passport / Driving Licence)</div>
          <p className="text-xs text-muted-foreground mb-3">Renters upload just one of the three types — this single rule and size limit applies no matter which they pick.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Status</Label>
              <select
                className="mt-1.5 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={identityDocSettings.status || "required"}
                onChange={(e) => updateIdentityDocSetting("status", e.target.value)}
                data-testid="identity-doc-status"
              >
                <option value="required">Required</option>
                <option value="optional">Optional</option>
                <option value="not_necessary">Not Necessary</option>
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Max size (MB)</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <Input
                  type="number"
                  min={1}
                  disabled={identityDocSettings.max_size_mb === null || identityDocSettings.max_size_mb === undefined}
                  value={identityDocSettings.max_size_mb ?? ""}
                  onChange={(e) => updateIdentityDocSetting("max_size_mb", e.target.value === "" ? null : Number(e.target.value))}
                  data-testid="identity-doc-maxsize"
                />
                <label className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={identityDocSettings.max_size_mb === null || identityDocSettings.max_size_mb === undefined}
                    onChange={(e) => updateIdentityDocSetting("max_size_mb", e.target.checked ? null : 10)}
                  />
                  No limit
                </label>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Identity verification mode</Label>
            <select
              className="mt-1.5 w-full h-9 rounded-md border border-input bg-background px-3 text-sm max-w-md"
              value={identityVerificationMode}
              onChange={(e) => setIdentityVerificationMode(e.target.value)}
              data-testid="identity-verification-mode-select"
            >
              <option value="backend">Backend (flexible — OCR runs in background, never blocks)</option>
              <option value="frontend">Frontend (strict — OCR runs immediately, failure blocks)</option>
            </select>
          </div>
        </div>

        <div className="mt-6 border-t border-border pt-6">
          <div className="font-medium text-sm mb-2">Car listing photo count</div>
          <p className="text-xs text-muted-foreground mb-3">Minimum and maximum number of photos an owner must upload per car listing.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Minimum images</Label>
              <Input
                type="number"
                min={1}
                value={imageSettings.min_images ?? 4}
                onChange={(e) => updateImageSetting("min_images", e.target.value === "" ? 0 : Number(e.target.value))}
                data-testid="image-min-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Maximum images</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <Input
                  type="number"
                  min={1}
                  disabled={imageSettings.max_images === null || imageSettings.max_images === undefined}
                  value={imageSettings.max_images ?? ""}
                  onChange={(e) => updateImageSetting("max_images", e.target.value === "" ? null : Number(e.target.value))}
                  data-testid="image-max-input"
                />
                <label className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={imageSettings.max_images === null || imageSettings.max_images === undefined}
                    onChange={(e) => updateImageSetting("max_images", e.target.checked ? null : 10)}
                  />
                  No limit
                </label>
              </div>
            </div>
          </div>
        </div>

        <Button onClick={saveDocumentSettings} className="mt-4 bg-primary hover:bg-primary/90" data-testid="document-settings-save-btn">Save document settings</Button>
      </Card>

      <PaymentGatewaysPanel />
      <PayoutDestinationsPanel />
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <DashboardLayout nav={nav} title="Admin console" subtitle="Operations & configuration">
      <Routes>
        <Route index element={<Overview />} />
        <Route path="users" element={<UsersPanel />} />
        <Route path="kyc" element={<AdminKycPanel />} />
        <Route path="cars" element={<CarsPanel />} />
        <Route path="sponsorship" element={<SponsorshipOrdersPanel />} />
        <Route path="bookings" element={<BookingsPanel />} />
        <Route path="history" element={<HistoryPanel />} />
        <Route path="flagged" element={<FlaggedReviewsPanel />} />
        <Route path="countries" element={<CountriesPanel />} />
        <Route path="currencies" element={<CurrenciesPanel />} />
        <Route path="rates" element={<RatesPanel />} />
        <Route path="settings" element={<SettingsPanel />} />
        <Route path="support" element={<SupportPanel />} />
      </Routes>
    </DashboardLayout>
  );
}


function HistoryPanel() {
  const [entries, setEntries] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const actorId = searchParams.get("actor_id");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [messageTarget, setMessageTarget] = useState(null);

  const load = () => {
    setLoading(true);
    const params = filter ? { entity_type: filter } : {};
    if (actorId) params.actor_id = actorId;
    api.get("/admin/history", { params }).then(({ data }) => setEntries(data)).finally(() => setLoading(false));
  };
  useEffect(load, [filter, actorId]);
  const actorName = entries.find((e) => e.actor_id === actorId)?.actor_name;

  const ENTITY_LABELS = { user: "User", car: "Car", booking: "Booking" };
  const ENTITY_COLORS = { user: "bg-blue-100 text-blue-800", car: "bg-purple-100 text-purple-800", booking: "bg-amber-100 text-amber-800" };

  return (
    <Card className="border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl font-semibold tracking-tight">History</h3>
        <div className="flex gap-1.5">
          {[["", "All"], ["user", "Users"], ["car", "Cars"], ["booking", "Bookings"]].map(([val, label]) => (
            <button key={val} type="button" onClick={() => setFilter(val)}
              className={"px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors " + (filter === val ? "bg-[#2A9D8F] text-white border-[#2A9D8F]" : "border-border text-muted-foreground hover:bg-muted")}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {actorId && (
        <div className="flex items-center justify-between bg-muted/50 border border-border rounded-lg px-3 py-2 mb-4 text-xs">
          <span>Showing activity for <strong>{actorName || "this user"}</strong></span>
          <button type="button" onClick={() => setSearchParams({})} className="text-[#2A9D8F] font-semibold hover:underline">
            Clear filter
          </button>
        </div>
      )}
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center">No activity recorded yet.</div>
      ) : (
        <div className="space-y-3" data-testid="history-list">
          {entries.map((h) => (
            <div key={h.id} className="border-b border-border last:border-b-0 pb-3" data-testid={`history-${h.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`text-[10px] ${ENTITY_COLORS[h.entity_type] || ""}`}>{ENTITY_LABELS[h.entity_type] || h.entity_type}</Badge>
                <span className="font-medium text-sm">{h.title}</span>
              </div>
              {h.description && <div className="text-xs text-muted-foreground mt-1">{h.description}</div>}
              <div className="flex items-center gap-2 mt-1">
                <div className="text-[10px] text-muted-foreground/70">
                  {h.actor_name && <>By {h.actor_name} · </>}{new Date(h.created_at).toLocaleString()}
                </div>
                {h.actor_id && <ContactIcons email={h.actor_email} phone={h.actor_phone} userId={h.actor_id} name={h.actor_name} isGuest={false} setMessageTarget={setMessageTarget} />}
              </div>
            </div>
          ))}
        </div>
      )}
      <MessageComposeDialog messageTarget={messageTarget} setMessageTarget={setMessageTarget} />
    </Card>
  );
}

function FlaggedReviewsPanel() {
  const [data, setData] = useState({ flagged_reviews: [], flagged_owners: [], flagged_customers: [] });
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/reviews/flagged").then(({ data }) => setData(data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const confirmDelete = async () => {
    if (!reason.trim()) { toast.error("Please provide a reason"); return; }
    setDeleting(true);
    try {
      await api.delete(`/admin/reviews/${deleteTarget.id}`, { data: { reason } });
      toast.success("Review deleted — author notified");
      setDeleteTarget(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6">
      <Card className="border-border p-6">
        <h3 className="font-display text-xl font-semibold tracking-tight mb-4">Flagged reviews (1–2 stars)</h3>
        {data.flagged_reviews.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">Nothing flagged right now.</div>
        ) : (
          <div className="space-y-3" data-testid="flagged-reviews-list">
            {data.flagged_reviews.map((r) => (
              <div key={r.id} className="border-b border-border last:border-b-0 pb-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Badge className="capitalize">{r.type === "customer_rating" ? "Customer rating" : "Car review"}</Badge>
                    <span className="font-medium text-sm">{r.rating}★ — {r.customer_name || r.owner_name || "Unknown"}</span>
                  </div>
                  <Button size="sm" variant="outline" className="text-destructive hover:bg-destructive/10" onClick={() => setDeleteTarget(r)} data-testid={`delete-review-${r.id}`}>
                    Delete
                  </Button>
                </div>
                {r.comment && <div className="text-xs text-muted-foreground mt-1">{r.comment}</div>}
                <div className="text-[10px] text-muted-foreground/70 mt-1">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="border-border p-6">
        <h3 className="font-display text-xl font-semibold tracking-tight mb-4">Owners with a low average rating</h3>
        {data.flagged_owners.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">None flagged.</div>
        ) : (
          <div className="space-y-2">
            {data.flagged_owners.map((u) => (
              <div key={u.id} className="flex items-center justify-between text-sm border-b border-border last:border-b-0 pb-2">
                <span>{u.name} <span className="text-muted-foreground">({u.email})</span></span>
                <Badge variant="outline">{u.owner_rating != null ? u.owner_rating.toFixed(1) : "—"} ★ · {u.owner_rating_count} rating{u.owner_rating_count === 1 ? "" : "s"}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="border-border p-6">
        <h3 className="font-display text-xl font-semibold tracking-tight mb-4">Customers with a low average rating</h3>
        {data.flagged_customers.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">None flagged.</div>
        ) : (
          <div className="space-y-2">
            {data.flagged_customers.map((u) => (
              <div key={u.id} className="flex items-center justify-between text-sm border-b border-border last:border-b-0 pb-2">
                <span>{u.name} <span className="text-muted-foreground">({u.email})</span></span>
                <Badge variant="outline">{u.customer_rating != null ? u.customer_rating.toFixed(1) : "—"} ★ · {u.customer_rating_count} rating{u.customer_rating_count === 1 ? "" : "s"}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) { setDeleteTarget(null); setReason(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this review?</DialogTitle>
            <DialogDescription>The person who wrote it will be notified with the reason you give below — this can't be undone.</DialogDescription>
          </DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for removal (shown to the author)..." rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setReason(""); }}>Cancel</Button>
            <Button className="bg-destructive hover:bg-destructive/90 text-white" onClick={confirmDelete} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete & notify"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function SupportPanel() {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/admin/support/tickets").then(({ data }) => setTickets(data));
  };
  useEffect(load, []);

  const filtered = tickets.filter((t) => !filter || t.status === filter);

  const openTicket = async (t) => {
    const { data } = await api.get(`/support/tickets/${t.id}`);
    setSelected(data);
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      const { data } = await api.post(`/support/tickets/${selected.id}/reply`, { message: reply });
      setSelected(data);
      setReply("");
      toast.success("Reply sent");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const toggleStatus = async (t) => {
    const newStatus = t.status === "open" ? "resolved" : "open";
    try {
      await api.patch(`/admin/support/tickets/${t.id}`, { status: newStatus });
      setTickets(tickets.map((x) => x.id === t.id ? { ...x, status: newStatus } : x));
      if (selected?.id === t.id) setSelected({ ...selected, status: newStatus });
      toast.success(newStatus === "resolved" ? "Marked resolved" : "Reopened");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="border-border p-6" data-testid="admin-support-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="font-display text-xl font-semibold tracking-tight">Support messages ({tickets.length})</h3>
        <div className="flex gap-2">
          <Button size="sm" variant={filter === "" ? "default" : "outline"} onClick={() => setFilter("")}>All</Button>
          <Button size="sm" variant={filter === "open" ? "default" : "outline"} onClick={() => setFilter("open")}>Open</Button>
          <Button size="sm" variant={filter === "resolved" ? "default" : "outline"} onClick={() => setFilter("resolved")}>Resolved</Button>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow><TableHead>Subject</TableHead><TableHead>From</TableHead><TableHead>Last activity</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((t) => (
            <TableRow key={t.id} data-testid={`support-ticket-row-${t.id}`}>
              <TableCell className="font-medium">{t.subject}</TableCell>
              <TableCell className="text-muted-foreground">{t.name} ({t.email})</TableCell>
              <TableCell className="text-sm text-muted-foreground">{new Date(t.updated_at).toLocaleString()}</TableCell>
              <TableCell><Badge variant={t.status === "open" ? "default" : "outline"} className="capitalize">{t.status}</Badge></TableCell>
              <TableCell className="space-x-2 whitespace-nowrap">
                <Button size="sm" variant="outline" onClick={() => openTicket(t)}>View</Button>
                <Button size="sm" variant="ghost" onClick={() => toggleStatus(t)}>{t.status === "open" ? "Mark resolved" : "Reopen"}</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="admin-ticket-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{selected.subject}</DialogTitle>
                <DialogDescription>From {selected.name} ({selected.email})</DialogDescription>
              </DialogHeader>
              <div className="max-h-80 overflow-y-auto space-y-3 my-2">
                {selected.messages.map((m, i) => (
                  <div key={i} className={`p-3 rounded-md text-sm ${m.sender === "admin" ? "bg-primary/10" : "bg-muted"}`}>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                      {m.sender === "admin" ? "You (Admin)" : selected.name} · {new Date(m.created_at).toLocaleString()}
                    </div>
                    <p className="whitespace-pre-wrap">{m.body}</p>
                  </div>
                ))}
              </div>
              <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type a reply…" data-testid="admin-ticket-reply-input" />
              <DialogFooter>
                <Button onClick={sendReply} disabled={sending || !reply.trim()} className="bg-primary hover:bg-primary/90" data-testid="admin-ticket-reply-btn">
                  {sending ? "Sending…" : "Send reply"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
